/**
 * Practitioner formula builder (PRA-020, PRA-021, PRA-022): intake, generate, edit within engine
 * limits, and submit for a Lattice physician.
 */
import * as stylex from "@stylexjs/stylex";
import { useEffect, useRef, useState } from "react";
import type { ClinicFormulaCatalogItem, ClinicFormulaIngredient, ClinicFormulaView, ClinicPatientList, IntakeAnswers } from "@lattice/core";
import { biomarker, isBiomarkerCode } from "@lattice/core";
import { formulaPill } from "@lattice/format";
import "@lattice/tokens/breakpoints.stylex";
import { breakpoints } from "@lattice/tokens/breakpoints.stylex";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import { Banner } from "@lattice/ui/Banner";
import { Button } from "@lattice/ui/Button";
import { ConfirmDialog } from "@lattice/ui/ConfirmDialog";
import { EmptyState } from "@lattice/ui/EmptyState";
import { ErrorState } from "@lattice/ui/ErrorState";
import { DateInput } from "@lattice/ui/DateInput";
import { Field } from "@lattice/ui/Field";
import { Input } from "@lattice/ui/Input";
import { Pending } from "@lattice/ui/Pending";
import { CheckIcon } from "@lattice/ui/icons";
import { Section } from "@lattice/ui/Section";
import { Select } from "@lattice/ui/Select";
import { Status } from "@lattice/ui/Status";
import { StatusPill } from "@lattice/ui/StatusPill";
import { Text } from "@lattice/ui/Text";
import type { ClinicFormulaLoad } from "../api.ts";
import { draftClinicFormula, editClinicFormula, loadClinicFormula, saveClinicFormulaIntake, submitClinicFormula } from "../api.ts";
import { navigate } from "../router.ts";
import { LeaveGuard } from "./LeaveGuard.tsx";
import { NotOpened } from "./NotOpened.tsx";
import { RecordControls, RecordList, RecordRow } from "./Record.tsx";
import { ClinicShell } from "./Shell.tsx";
const NO_FORMULA = {
  label: "No formula",
  tone: "neutral"
} as const;

/** What the practitioner does next, beside the pill that names where the formula stands. */
const NEXT: Readonly<Record<ClinicFormulaView["status"], string>> = {
  none: "No formula yet.",
  draft: "Edit and submit when it is ready for a physician.",
  in_review: "Waiting for a Lattice physician to review this formula.",
  approved: "A physician signed this formula.",
  declined: "A physician declined this formula."
};
function StatusLine({
  status
}: {
  readonly status: ClinicFormulaView["status"];
}) {
  return <div className="x78zum5 x1a02dak x6s0dn4 x626il7">
      <StatusPill pill={status === "none" ? NO_FORMULA : formulaPill(status)} />
      <Text tone="secondary">{NEXT[status]}</Text>
    </div>;
}
type Screen = {
  readonly kind: "loading";
} | {
  readonly kind: "refused";
  readonly why: "missing" | "forbidden" | "failed" | "pending";
} | {
  readonly kind: "ready";
  readonly formula: ClinicFormulaView;
};
type MutableIntake = { -readonly [K in keyof IntakeAnswers]: IntakeAnswers[K] };
function listField(value: string): string[] {
  return value.split(",").map(part => part.trim()).filter(part => part.length > 0);
}

/** The health details as the practitioner typed them. */
interface IntakeForm {
  readonly dateOfBirth: string;
  readonly sex: string;
  readonly heightCm: string;
  readonly weightKg: string;
  readonly exerciseDays: string;
  readonly exerciseType: string;
  readonly diet: string;
  readonly goal: string;
  readonly conditions: string;
  readonly medications: string;
  readonly allergies: string;
}
const blankForm: IntakeForm = {
  dateOfBirth: "",
  sex: "",
  heightCm: "",
  weightKg: "",
  exerciseDays: "",
  exerciseType: "",
  diet: "",
  goal: "",
  conditions: "",
  medications: "",
  allergies: ""
};
function formOf(formula: ClinicFormulaView): IntakeForm {
  const {
    intake
  } = formula;
  return {
    dateOfBirth: intake.dateOfBirth ?? "",
    sex: intake.sex ?? "",
    heightCm: intake.heightCm === null ? "" : String(intake.heightCm),
    weightKg: intake.weightKg === null ? "" : String(intake.weightKg),
    exerciseDays: intake.exerciseDaysPerWeek === null ? "" : String(intake.exerciseDaysPerWeek),
    exerciseType: intake.exerciseType ?? "",
    diet: intake.diet ?? "",
    goal: intake.goal ?? "",
    conditions: intake.conditions.join(", "),
    medications: intake.medications.join(", "),
    allergies: intake.allergies.join(", ")
  };
}
function sameForm(a: IntakeForm, b: IntakeForm): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
function answersFromForm(form: IntakeForm): IntakeAnswers {
  /* A blank box is no answer. `Number("")` is 0, which would be sent as a height of zero. */
  const measure = (value: string): number | null => value.trim() === "" || !Number.isFinite(Number(value)) ? null : Number(value);
  const heightCm = measure(form.heightCm);
  const weightKg = measure(form.weightKg);
  const exerciseDays = measure(form.exerciseDays);
  const answers: MutableIntake = {
    conditions: listField(form.conditions),
    medications: listField(form.medications),
    allergies: listField(form.allergies)
  };
  if (form.dateOfBirth !== "") answers.date_of_birth = {
    date: form.dateOfBirth
  };
  if (form.sex === "female" || form.sex === "male") answers.sex = form.sex;
  if (heightCm !== null && weightKg !== null) answers.height_weight = {
    heightCm,
    weightKg
  };
  if (exerciseDays !== null && (form.exerciseType === "none" || form.exerciseType === "strength" || form.exerciseType === "endurance" || form.exerciseType === "mixed" || form.exerciseType === "other")) answers.exercise = {
    daysPerWeek: exerciseDays,
    type: form.exerciseType
  };
  if (form.diet === "omnivore" || form.diet === "pescatarian" || form.diet === "vegetarian" || form.diet === "vegan" || form.diet === "other") answers.diet = form.diet;
  if (form.goal === "general" || form.goal === "energy" || form.goal === "strength" || form.goal === "weight" || form.goal === "other") answers.goal = form.goal;
  return answers;
}
const SEX = [{
  value: "female",
  label: "Female"
}, {
  value: "male",
  label: "Male"
}] as const;
const EXERCISE = [{
  value: "none",
  label: "None"
}, {
  value: "strength",
  label: "Strength"
}, {
  value: "endurance",
  label: "Endurance"
}, {
  value: "mixed",
  label: "Mixed"
}, {
  value: "other",
  label: "Other"
}] as const;
const DIET = [{
  value: "omnivore",
  label: "Omnivore"
}, {
  value: "pescatarian",
  label: "Pescatarian"
}, {
  value: "vegetarian",
  label: "Vegetarian"
}, {
  value: "vegan",
  label: "Vegan"
}, {
  value: "other",
  label: "Other"
}] as const;
const GOAL = [{
  value: "general",
  label: "General"
}, {
  value: "energy",
  label: "Energy"
}, {
  value: "strength",
  label: "Strength"
}, {
  value: "weight",
  label: "Weight"
}, {
  value: "other",
  label: "Other"
}] as const;

/** The intake answers the API names when a draft cannot start, in the words a practitioner uses. */
const INTAKE_LABEL = new Map([["date_of_birth", "date of birth"], ["sex", "sex"], ["height_weight", "height and weight"], ["exercise", "training days and type"], ["diet", "diet"], ["goal", "goal"], ["conditions", "conditions"], ["medications", "medications"], ["allergies", "allergies"]]);

/** "date of birth, sex and goal". */
function namedMissing(missing: readonly string[]): string {
  const names = missing.map(key => INTAKE_LABEL.get(key) ?? key.replaceAll("_", " "));
  const last = names.pop();
  if (last === undefined) return "";
  return names.length === 0 ? last : `${names.join(", ")} and ${last}`;
}

/** `all` with the message for `ingredientId` replaced, or taken away when `next` is null. */
function withOutcome(all: Readonly<Record<string, Outcome>>, ingredientId: string | undefined, next: Outcome | null): Readonly<Record<string, Outcome>> {
  const rest = Object.fromEntries(Object.entries(all).filter(([id]) => id !== ingredientId));
  return next === null || ingredientId === undefined ? rest : {
    ...rest,
    [ingredientId]: next
  };
}

/** What the formula page says when the load itself was refused or got no answer. */
const REFUSED = {
  forbidden: {
    heading: "Your role can't open this formula",
    body: "Formulas are for the clinic's practitioners. Ask the clinic owner to change your role if you need this."
  },
  failed: {
    heading: "We couldn't load the formula",
    body: "Try again in a minute. If it keeps happening, write to support."
  },
  missing: {
    heading: "We couldn't open that formula",
    body: "The patient may not be linked to this clinic."
  },
  pending: {
    heading: "Waiting for the patient",
    body: "The patient has not accepted this clinic's request yet. The formula opens once they do."
  }
} as const;

/** The screen a load answer leads to. */
function screenAfterLoad(loaded: ClinicFormulaLoad): Screen {
  if (loaded.status === "ok") return {
    kind: "ready",
    formula: loaded.formula
  };
  return {
    kind: "refused",
    why: loaded.status
  };
}
const LICENCE_UNVERIFIED = "Lattice staff verify a clinic's licence before it can submit formulas. You can keep editing this draft until then.";

/** Why a submit did not go through. */
function submitProblem(status: "unverified" | "pending" | "missing" | "forbidden" | "failed" | "signed_out"): string {
  if (status === "unverified") return LICENCE_UNVERIFIED;
  if (status === "forbidden") return "Your role in this clinic can't submit formulas.";
  if (status === "failed") return "We couldn't reach Lattice. Check your connection and try again.";
  return "We couldn't submit this formula.";
}

/** How a try at a draft ended: made, stopped by health details to fix, or failed another way. */
type DraftAnswer = {
  readonly kind: "made";
} | {
  readonly kind: "details";
  readonly text: string;
} | {
  readonly kind: "failed";
  readonly text: string;
};

/** The engine's reasons for a refusal, in one line. */
function notesOf(violations: ClinicFormulaView["violations"]): string {
  return violations.map(violation => violation.note).join(" ");
}

/** What a saved dose says beside its field. */
function savedDose(saved: ClinicFormulaView, ingredientId: string): string {
  const item = saved.ingredients.find(row => row.ingredientId === ingredientId);
  return item === undefined ? "Saved." : `Saved. ${item.name} is ${String(item.dose)} ${item.unit}.`;
}

/** The answer of `run`, or nothing if it threw, which is what a dropped connection does to a fetch. */
async function attempt<T>(run: () => Promise<T>): Promise<T | undefined> {
  try {
    return await run();
  } catch {
    return undefined;
  }
}

/** How an edit of a dose ended: saved, turned down by the engine, or never answered. */
type DoseResult = "saved" | "refused" | "unreached";

/** What happened to the last edit of an ingredient, shown beside it. */
interface Outcome {
  readonly ingredientId: string;
  readonly kind: "saved" | "refused" | "unsent";
  readonly text: string;
}

/**
 * Why a dose was refused, in words about the number the practitioner typed. The reasons come from
 * the refusal itself, because the saved formula never held the refused dose and has none.
 */
function refusedDose(saved: ClinicFormulaView, ingredientId: string, violations: ClinicFormulaView["violations"]): string {
  const item = saved.ingredients.find(row => row.ingredientId === ingredientId);
  const limits = saved.catalog.find(row => row.id === ingredientId);
  const stays = item === undefined ? "" : ` The saved dose is still ${String(item.dose)} ${item.unit}.`;
  if (item === undefined || limits === undefined) return `Not saved. ${notesOf(violations)}${stays}`;
  const kinds = violations.map(violation => violation.kind);
  if (kinds.includes("above_maximum")) return `Not saved. ${item.name} is limited to ${String(limits.dailyMax)} ${item.unit} a day.${stays}`;
  if (kinds.includes("below_minimum")) return `Not saved. ${item.name} needs at least ${String(limits.dailyMin)} ${item.unit} a day.${stays}`;
  return `Not saved. ${notesOf(violations)}${stays}`;
}

/** What the dose field is in and how far it may go, once an ingredient is chosen. */
function doseHint(item: ClinicFormulaCatalogItem | undefined): string {
  if (item === undefined) return "Choose an ingredient first.";
  return `In ${item.unit} a day, from ${String(item.dailyMin)} to ${String(item.dailyMax)}.`;
}

/** The sentence the engine wrote to the customer, said to the practitioner about the patient. */
function toPractitioner(sentence: string): string {
  const said = sentence.replace(/^Your /, "The patient's ").replace(/\byour lab's\b/g, "the lab's").replace(/\byour\b/g, "the patient's");
  return said.startsWith("The patient's ") ? said.replace(/^The patient's (\S)/, (_, first: string) => first.toUpperCase()) : said;
}
function markerName(code: string): string {
  return isBiomarkerCode(code) ? biomarker(code).name : code;
}
function markerUnit(code: string): string {
  return isBiomarkerCode(code) ? ` ${biomarker(code).unit}` : "";
}

/** "22 Sep 2026" from a stored date, or the stored text when it is not a date. */
function readableDate(stored: string): string {
  const day = /^\d{4}-\d{2}-\d{2}/.exec(stored)?.[0];
  if (day === undefined) return stored;
  return new Date(`${day}T00:00:00Z`).toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC"
  });
}
function observationText(observation: ClinicFormulaIngredient["observations"][number]): string {
  const reading = `${markerName(observation.code)} ${String(observation.value)}${markerUnit(observation.code)}`;
  return observation.collectedOn === null ? reading : `${reading}, ${readableDate(observation.collectedOn)}`;
}
function IngredientRow({
  item,
  warnings,
  outcome,
  editable,
  busy,
  onDose,
  onType,
  onRemove
}: {
  readonly item: ClinicFormulaIngredient;
  readonly warnings: readonly string[];
  readonly outcome: Outcome | undefined;
  readonly editable: boolean;
  readonly busy: boolean;
  readonly onDose: (dose: number) => Promise<DoseResult>;
  readonly onType: () => void;
  readonly onRemove: () => void;
}) {
  const [value, setValue] = useState(String(item.dose));
  /* The entry that was last turned down, so leaving the field again does not send it twice. */
  const [turnedDown, setTurnedDown] = useState<string | null>(null);
  /* Enter and then leaving the field would otherwise send the same dose twice. */
  const sending = useRef(false);
  useEffect(() => setValue(String(item.dose)), [item.dose]);

  /* A blank or unreadable entry is not a dose of zero, so the field goes back to the saved one. */
  function commit() {
    const dose = Number(value);
    if (value.trim() === "" || !Number.isFinite(dose)) {
      setValue(String(item.dose));
      return;
    }
    if (dose === item.dose || value === turnedDown || sending.current) return;
    sending.current = true;
    void onDose(dose).then(result => {
      sending.current = false;

      /* The entry stays in the field beside the reason, so it can be corrected rather than retyped.
         A dose that never reached Lattice is not remembered as turned down, so leaving the field
         again tries it again. */
      if (result === "refused") setTurnedDown(value);
    });
  }
  const refused = outcome?.kind === "refused";
  return <RecordRow title={item.name} detail={item.separateAddon ? "Ships separately" : undefined} trailing={editable ? <RecordControls>
            <span className="xhnqznf x193iq5w">
              <Field label={`${item.name} dose`} id={`dose-field-${item.ingredientId}`}>
                {control => <Input {...control} type="number" step="any" suffix={item.unit} aria-invalid={refused ? true : undefined} aria-describedby={`refusal-${item.ingredientId}`} value={value}
        /* Read-only rather than disabled while a save runs, so a tap on another dose keeps its focus. */ readOnly={busy} testId={`dose-${item.ingredientId}`} onChange={next => {
          setValue(next);
          setTurnedDown(null);
          onType();
        }} onBlur={commit} onKeyDown={event => {
          if (event.key === "Enter") {
            event.preventDefault();
            commit();
          }
          if (event.key === "Escape") {
            setValue(String(item.dose));
            setTurnedDown(null);
            onType();
          }
        }} />}
              </Field>
            </span>
            <Button variant="secondary" corners="rounded" size="md" loading={busy} ariaLabel={`Remove ${item.name}`} onClick={onRemove}>
              Remove
            </Button>
          </RecordControls> : <Text as="span">
            {item.dose} {item.unit}
          </Text>}>
      {/* Both are always in the page, so a screen reader announces text put into them. */}
      <div className="x78zum5 xdt5ytf xskm3c9">
        <div role="alert">
          <Text variant="caption" tone="danger" id={`refusal-${item.ingredientId}`} testId={`refusal-${item.ingredientId}`}>
            {refused || outcome?.kind === "unsent" ? outcome.text : ""}
          </Text>
        </div>
        <div role="status">
          <Text variant="caption" tone="secondary" testId={`outcome-${item.ingredientId}`}>
            {outcome?.kind === "saved" ? <span className="x78zum5 x6s0dn4 xskm3c9">
                <span className="x1p2vmyc x2lah0s">
                  <CheckIcon />
                </span>
                {outcome.text}
              </span> : ""}
          </Text>
        </div>
      </div>
      {item.sentence === "" ? null : <Text variant="caption" tone="secondary">
          {toPractitioner(item.sentence)}
        </Text>}
      {item.observations.length === 0 ? null : <Text variant="caption" tone="secondary">
          Based on {item.observations.map(observationText).join("; ")}
        </Text>}
      {warnings.map(warning => <Banner key={warning} tone="warn" title="Check this">
          {warning}
        </Banner>)}
    </RecordRow>;
}
function ChangeBanner({
  patientId,
  acknowledged
}: {
  readonly patientId: string;
  readonly acknowledged: boolean;
}) {
  return <Banner tone={acknowledged ? "info" : "warn"} title={acknowledged ? "Changed by a physician" : "Review needed"} action={<Button variant="secondary" corners="rounded" size="md" testId="open-changes" onClick={() => navigate(`/clinic/patients/${patientId}/formula/changes`)}>
          {acknowledged ? "View physician changes" : "A physician changed this formula"}
        </Button>}>
      {acknowledged ? "You have reviewed what the physician changed." : "Review what changed before the patient is invited."}
    </Banner>;
}
function AddIngredient({
  unused,
  busy,
  problem,
  onAdd
}: {
  readonly unused: readonly ClinicFormulaCatalogItem[];
  readonly busy: boolean;
  readonly problem: string | null;
  readonly onAdd: (ingredientId: string, dose: string) => Promise<boolean>;
}) {
  const [id, setId] = useState("");
  const [dose, setDose] = useState("");
  const chosen = unused.find(item => item.id === id);
  return <Section title="Add from the formulary">
      <form noValidate className="x78zum5 xdt5ytf x1r0x45p" onSubmit={event => {
      event.preventDefault();
      if (busy) return;
      void onAdd(id, dose).then(added => {
        if (!added) return;
        setId("");
        setDose("");
      });
    }}>
        <div className="xrvj5dj x1y6fwsi x1fjms2 x1r0x45p">
          <Field label="Ingredient" id="add-ingredient-choice">
            {control => <Select {...control} placeholder="Choose an ingredient" options={unused.map(item => ({
            value: item.id,
            label: `${item.name} (${String(item.dailyMin)}\u2013${String(item.dailyMax)} ${item.unit})`
          }))} value={id} onChange={setId} />}
          </Field>
          <Field label="Dose" id="add-ingredient-dose" hint={doseHint(chosen)}>
            {control => <Input {...control} inputMode="decimal" value={dose} onChange={setDose} />}
          </Field>
        </div>
        <div>
          <Button type="submit" variant="secondary" corners="rounded" testId="add-ingredient" loading={busy}>
            Add ingredient
          </Button>
        </div>
        {problem === null ? null : <ErrorState title="Not added" body={problem} />}
      </form>
    </Section>;
}
function HealthForm({
  form,
  onChange,
  busy,
  problem,
  saved,
  onSave
}: {
  readonly form: IntakeForm;
  readonly onChange: (form: IntakeForm) => void;
  readonly busy: boolean;
  readonly problem: string | null;
  readonly saved: boolean;
  readonly onSave: () => void;
}) {
  const set = (key: keyof IntakeForm) => (value: string) => onChange({
    ...form,
    [key]: value
  });
  return <form noValidate className="x78zum5 xdt5ytf x1r0x45p" onSubmit={event => {
    event.preventDefault();
    if (!busy) onSave();
  }}>
      <div className="xrvj5dj x1y6fwsi x1fjms2 x1r0x45p">
        <Field label="Date of birth" id="formula-dob" optional>
          {control => <DateInput {...control} mode="birthDate" value={form.dateOfBirth} onChange={set("dateOfBirth")} />}
        </Field>
        <Field label="Sex" id="formula-sex" optional>
          {control => <Select {...control} placeholder="Choose" options={SEX} value={form.sex} onChange={set("sex")} />}
        </Field>
        <Field label="Height (cm)" id="formula-height" optional>
          {control => <Input {...control} inputMode="decimal" value={form.heightCm} onChange={set("heightCm")} />}
        </Field>
        <Field label="Weight (kg)" id="formula-weight" optional>
          {control => <Input {...control} inputMode="decimal" value={form.weightKg} onChange={set("weightKg")} />}
        </Field>
        <Field label="Training days per week" id="formula-exercise-days" optional>
          {control => <Input {...control} inputMode="numeric" value={form.exerciseDays} onChange={set("exerciseDays")} />}
        </Field>
        <Field label="Training type" id="formula-exercise-type" optional>
          {control => <Select {...control} placeholder="Choose" options={EXERCISE} value={form.exerciseType} onChange={set("exerciseType")} />}
        </Field>
        <Field label="Diet" id="formula-diet" optional>
          {control => <Select {...control} placeholder="Choose" options={DIET} value={form.diet} onChange={set("diet")} />}
        </Field>
        <Field label="Goal" id="formula-goal" optional>
          {control => <Select {...control} placeholder="Choose" options={GOAL} value={form.goal} onChange={set("goal")} />}
        </Field>
        <Field label="Conditions" id="formula-conditions" hint="Separate with commas." optional>
          {control => <Input {...control} value={form.conditions} onChange={set("conditions")} />}
        </Field>
        <Field label="Medications" id="formula-medications" hint="Separate with commas." optional>
          {control => <Input {...control} value={form.medications} onChange={set("medications")} />}
        </Field>
        <Field label="Allergies" id="formula-allergies" hint="Separate with commas." optional>
          {control => <Input {...control} value={form.allergies} onChange={set("allergies")} />}
        </Field>
      </div>
      <div>
        <Button type="submit" variant="secondary" corners="rounded" loading={busy}>
          Save health details
        </Button>
      </div>
      {problem === null ? null : <ErrorState title="Not saved" body={problem} />}
      {saved ? <Banner tone="ok" title="Saved">
          The health details are saved.
        </Banner> : null}
    </form>;
}
function Outcome({
  note
}: {
  readonly note: string | null;
}) {
  /* The region is on the page before it has a note, so a screen reader announces the one that
     arrives, as it may not for a region that mounts with its text. */
  return <div role="status">
      {note === null ? null : <Status state="ok" live={false}>
          {note}
        </Status>}
    </div>;
}
function FormulaQuestions({
  asking,
  removing,
  onClose,
  onRemove,
  onRegenerate,
  onSubmit
}: {
  readonly asking: "regenerate" | "submit" | "remove" | null;
  readonly removing: ClinicFormulaIngredient | null;
  readonly onClose: () => void;
  readonly onRemove: (item: ClinicFormulaIngredient) => Promise<string | null>;
  readonly onRegenerate: () => Promise<string | null>;
  readonly onSubmit: () => Promise<string | null>;
}) {
  return <>
      <ConfirmDialog open={asking === "remove"} onClose={() => onClose()} title={`Remove ${removing?.name ?? "this ingredient"}?`} tone="danger" confirmLabel="Remove ingredient" cancelLabel="Keep ingredient" testId="remove-dialog" onConfirm={() => removing === null ? Promise.resolve(null) : onRemove(removing)}>
        It leaves the draft. You can add it back from the formulary below.
      </ConfirmDialog>
      <ConfirmDialog open={asking === "regenerate"} onClose={() => onClose()} title="Replace this draft?" tone="danger" confirmLabel="Regenerate draft" cancelLabel="Keep draft" testId="regenerate-dialog" onConfirm={onRegenerate}>
        A new draft is made from the health details above, and the doses you have edited are
        replaced. Nothing is sent to a physician.
      </ConfirmDialog>
      <ConfirmDialog open={asking === "submit"} onClose={() => onClose()} title="Submit this formula for review?" confirmLabel="Submit for review" cancelLabel="Keep editing" testId="submit-dialog" onConfirm={onSubmit}>
        A Lattice physician approves, edits or declines it, and their decision is final. You cannot
        edit the draft while it is in review.
      </ConfirmDialog>
    </>;
}
function SubmitSection({
  licenceVerified,
  busy,
  onSubmit
}: {
  readonly licenceVerified: boolean;
  readonly busy: boolean;
  readonly onSubmit: () => void;
}) {
  return <Section title="Submit for review">
      <Text tone="secondary">
        A Lattice physician approves, edits or declines. Their decision is final.
      </Text>
      <div>
        <Button testId="submit-formula" corners="rounded" loading={busy} disabled={!licenceVerified} describedBy={licenceVerified ? undefined : "licence-unverified"} onClick={onSubmit}>
          Submit for review
        </Button>
      </div>
      {licenceVerified ? null : <Text tone="secondary" id="licence-unverified" testId="licence-unverified">
          {LICENCE_UNVERIFIED}
        </Text>}
    </Section>;
}

/*
  Staff verify the licence in another window. Coming back to this one picks it up, and touches
  nothing else, so a dose or health detail being typed is not replaced.
*/
function awaitsLicence(screen: Screen): boolean {
  return screen.kind === "ready" && !screen.formula.licenceVerified;
}
function useLicenceRecheck(patientId: string, unverified: boolean, setScreen: (update: (now: Screen) => Screen) => void) {
  useEffect(() => {
    if (!unverified) return;
    let current = true;
    const recheck = () => {
      if (document.visibilityState !== "visible") return;
      void loadClinicFormula(patientId).then(loaded => {
        if (!current || loaded.status !== "ok" || !loaded.formula.licenceVerified) return;
        setScreen(now => now.kind === "ready" ? {
          kind: "ready",
          formula: {
            ...now.formula,
            licenceVerified: true
          }
        } : now);
      }, () => undefined);
    };
    window.addEventListener("focus", recheck);
    document.addEventListener("visibilitychange", recheck);
    return () => {
      current = false;
      window.removeEventListener("focus", recheck);
      document.removeEventListener("visibilitychange", recheck);
    };
  }, [unverified, patientId, setScreen]);
}
export function ClinicFormulaPage({
  patientId,
  clinic
}: {
  readonly patientId: string;
  readonly clinic: ClinicPatientList;
}) {
  const [screen, setScreen] = useState<Screen>({
    kind: "loading"
  });
  /* One message per control group, shown beside the group it is about. */
  const [editProblem, setEditProblem] = useState<string | null>(null);
  const [intakeProblem, setIntakeProblem] = useState<string | null>(null);
  const [draftProblem, setDraftProblem] = useState<string | null>(null);
  const [intakeSaved, setIntakeSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [outcomes, setOutcomes] = useState<Readonly<Record<string, Outcome>>>({});
  const [addProblem, setAddProblem] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [asking, setAsking] = useState<"regenerate" | "submit" | "remove" | null>(null);
  const [removing, setRemoving] = useState<ClinicFormulaIngredient | null>(null);
  /* A dose, add or remove is saving. The buttons that act on the whole draft stay pressable and
     wait for it instead of ignoring the press. */
  const [saving, setSaving] = useState(false);
  const [focusDraft, setFocusDraft] = useState(false);
  const inflight = useRef<Promise<unknown>>(Promise.resolve());
  const [form, setForm] = useState<IntakeForm>(blankForm);
  /* The fields as last saved, to tell typed-but-unsaved health details from saved ones. */
  const [saved, setSaved] = useState<IntakeForm | null>(null);
  const unsaved = saved !== null && !sameForm(form, saved);
  function fill(formula: ClinicFormulaView) {
    const next = formOf(formula);
    setSaved(next);
    setForm(next);
  }
  useEffect(() => {
    /* In development React runs this effect twice. Without the flag the second answer arrived
       after the practitioner had started typing and put the saved values back over theirs. */
    let current = true;
    void loadClinicFormula(patientId).then(loaded => {
      if (!current) return;
      if (loaded.status === "ok") fill(loaded.formula);
      setScreen(screenAfterLoad(loaded));
    }, () => {
      if (current) setScreen({
        kind: "refused",
        why: "failed"
      });
    });
    return () => {
      current = false;
    };
  }, [patientId]);
  useLicenceRecheck(patientId, awaitsLicence(screen), setScreen);
  if (screen.kind === "loading") return <ClinicShell title="Formula" signedIn clinicName={clinic.organizationName}>
        <Pending message="Loading the formula" bones={["title", "line", "line"]} />
      </ClinicShell>;
  if (screen.kind === "refused") return <NotOpened title={REFUSED[screen.why].heading} body={REFUSED[screen.why].body} retry={screen.why === "failed"} back={{
    label: "Back to patient",
    href: `/clinic/patients/${patientId}`
  }} />;
  const {
    formula
  } = screen;
  const editable = formula.status === "draft";
  const unused = formula.catalog.filter(item => !formula.ingredients.some(row => row.ingredientId === item.id));

  /* Health details are put back from the record only when the record is what the practitioner
     just saved or generated. An edit of a dose must leave details typed but not yet saved alone. */
  function apply(next: ClinicFormulaView, refill = false) {
    if (refill) fill(next);
    setScreen({
      kind: "ready",
      formula: next
    });
  }

  /** Runs a save of one ingredient, so a press on Submit or Regenerate can wait for it. */
  function track<T>(run: () => Promise<T>): Promise<T> {
    const started = run();
    inflight.current = Promise.all([inflight.current, started.catch(() => undefined)]);
    return started;
  }

  /* True once the health details are saved. Leaves the page busy for the caller to release. */
  async function saveIntake(): Promise<boolean> {
    setBusy(true);
    setIntakeProblem(null);
    setIntakeSaved(false);
    const result = await attempt(() => saveClinicFormulaIntake(patientId, answersFromForm(form)));
    if (result === undefined || result.status !== "ok") {
      setBusy(false);
      setIntakeProblem(result === undefined ? "We couldn't reach Lattice. Check your connection and try again." : "Check the health details, then try again.");
      return false;
    }
    apply(result.formula, true);
    return true;
  }
  async function onSaveIntake() {
    const saved = await saveIntake();
    setBusy(false);
    setIntakeSaved(saved);
  }

  /* Drafts from what was typed, so a failed save never starts a draft from older answers. */
  async function generate(): Promise<DraftAnswer> {
    setNote(null);
    if (!(await saveIntake())) return {
      kind: "details",
      text: "Fix the health details above, then generate again."
    };
    const result = await attempt(() => draftClinicFormula(patientId));
    setBusy(false);
    if (result === undefined) return {
      kind: "failed",
      text: "We couldn't reach Lattice. Check your connection and try again."
    };
    if (result.status === "incomplete") return {
      kind: "details",
      text: result.missing.length === 0 ? "Fill the required health details, then generate again." : `Add ${namedMissing(result.missing)} under health details, then generate again.`
    };
    if (result.status !== "ok") return {
      kind: "failed",
      text: "We couldn't generate a draft. Try again."
    };
    apply(result.formula, true);
    setNote("Draft made from the health details.");
    return {
      kind: "made"
    };
  }

  /* A first draft replaces nothing, so only a regeneration asks. The press waits for a dose that
     is still saving. */
  async function onGenerate() {
    await inflight.current;
    if (!hasDraft) {
      const answer = await generate();
      setDraftProblem(answer.kind === "made" ? null : answer.text);
    } else setAsking("regenerate");
  }

  /* Missing health details are fixed in the form the dialog covers, so the dialog closes and the
     page says it beside the fields. Any other refusal stays in the dialog. */
  async function regenerate(): Promise<string | null> {
    const answer = await generate();
    if (answer.kind === "failed") return answer.text;
    setDraftProblem(answer.kind === "details" ? answer.text : null);
    setAsking(null);
    return null;
  }
  function setOutcomeOf(ingredientId: string, next: Outcome | null) {
    setOutcomes(all => withOutcome(all, ingredientId, next));
  }

  /* What a dropped connection means for an edit that has no row of its own to say it on. */
  function notReached() {
    setEditProblem("We couldn't save that. Check your connection and try again.");
  }

  /*
    Starts an edit. A dropped connection must not leave the page busy for good with every field
    disabled, which is why every edit awaits `attempt`. `editing` names the ingredient whose message
    starts over, and no other ingredient's does.
  */
  function begin(editing?: string) {
    setBusy(true);
    setSaving(true);
    setFocusDraft(false);
    setEditProblem(null);
    setDraftProblem(null);
    setAddProblem(null);
    setOutcomes(all => withOutcome(all, editing, null));
  }
  async function changeDose(ingredientId: string, dose: number): Promise<DoseResult> {
    begin(ingredientId);
    const reply = await track(() => attempt(() => editClinicFormula(patientId, {
      ingredientId,
      change: "dose_changed",
      dose
    })));
    setBusy(false);
    setSaving(false);
    if (reply === undefined) {
      setOutcomeOf(ingredientId, {
        ingredientId,
        kind: "unsent",
        text: "Not saved. We couldn't reach Lattice. Check your connection and try again."
      });
      return "unreached";
    }
    if (reply.status === "blocked") {
      setOutcomeOf(ingredientId, {
        ingredientId,
        kind: "refused",
        text: refusedDose(reply.formula, ingredientId, reply.violations)
      });
      apply(reply.formula);
      return "refused";
    }
    if (reply.status !== "ok") {
      setEditProblem("That change is outside the engine's limits.");
      return "unreached";
    }
    setOutcomeOf(ingredientId, {
      ingredientId,
      kind: "saved",
      text: savedDose(reply.formula, ingredientId)
    });
    apply(reply.formula);
    return "saved";
  }

  /** The problem to show in the question, or null when the ingredient left the draft. */
  async function remove(item: ClinicFormulaIngredient): Promise<string | null> {
    begin(item.ingredientId);
    const result = await track(() => attempt(() => editClinicFormula(patientId, {
      ingredientId: item.ingredientId,
      change: "removed",
      dose: null
    })));
    setBusy(false);
    setSaving(false);
    if (result === undefined) return "We couldn't reach Lattice. Check your connection and try again.";
    if (result.status === "blocked") {
      apply(result.formula);
      return `Not removed. ${notesOf(result.violations)}`;
    }
    if (result.status !== "ok") return "That ingredient cannot be removed.";
    apply(result.formula);
    setNote(`Removed ${item.name}.`);

    /* The row that had focus is gone, so focus goes to the draft it left. Later than the question
       closing, which returns focus to the button that opened it. */
    window.setTimeout(() => setFocusDraft(true), 0);
    return null;
  }

  /* True once the ingredient is in, so the form can clear itself. */
  async function add(ingredientId: string, doseText: string): Promise<boolean> {
    const dose = Number(doseText);
    if (ingredientId === "") {
      setAddProblem("Choose an ingredient to add.");
      return false;
    }
    if (doseText.trim() === "" || !Number.isFinite(dose)) {
      setAddProblem("Enter the dose as a number.");
      return false;
    }
    begin();
    const result = await track(() => attempt(() => editClinicFormula(patientId, {
      ingredientId,
      change: "added",
      dose
    })));
    setBusy(false);
    setSaving(false);
    if (result === undefined) {
      notReached();
      return false;
    }
    if (result.status === "blocked") {
      setAddProblem(`Not added. ${notesOf(result.violations)}`);
      apply(result.formula);
      return false;
    }
    if (result.status !== "ok") {
      setEditProblem("That ingredient cannot be added at that dose.");
      return false;
    }
    apply(result.formula);
    return true;
  }

  /* The dialog shows a refusal itself and stays open, so the practitioner reads why. */
  async function submit(): Promise<string | null> {
    await inflight.current;
    setBusy(true);
    const result = await attempt(() => submitClinicFormula(patientId));
    setBusy(false);
    if (result === undefined) return submitProblem("failed");
    if (result.status !== "ok") return submitProblem(result.status);
    apply(result.formula);
    setNote("Sent. A Lattice physician will approve, edit or decline it.");
    return null;
  }
  const hasDraft = formula.status !== "none";
  return <ClinicShell title="Formula" crumbs={[{
    label: "Patients",
    href: "/clinic/patients"
  }, {
    label: formula.patientName ?? "Patient",
    href: `/clinic/patients/${patientId}`
  }]} signedIn clinicName={clinic.organizationName}>
      <StatusLine status={formula.status} />
      <LeaveGuard dirty={unsaved} what={`your changes to ${formula.patientName ?? "the patient"}'s health details`} />
      {clinic.roles.includes("practitioner") && formula.change !== null ? <ChangeBanner patientId={patientId} acknowledged={formula.change.acknowledged} /> : null}
      <Section title="Health details">
        <Text tone="secondary">
          Used to generate the draft. Conditions, medications and allergies feed the safety checks.
        </Text>
        <HealthForm form={form} onChange={setForm} busy={busy} problem={intakeProblem} saved={intakeSaved} onSave={() => void onSaveIntake()} />
      </Section>
      <Section title="Draft" focusTitle={focusDraft}>
        <Text tone="secondary">
          A dose saves when you leave its field or press Enter. Escape puts the saved dose back.
        </Text>
        {formula.status === "in_review" ? null : <div>
            <Button testId="generate-formula" corners="rounded" loading={busy && !saving} onClick={() => void onGenerate()}>
              {formula.status === "none" ? "Generate draft" : "Regenerate draft"}
            </Button>
          </div>}
        {draftProblem === null ? null : <ErrorState title="No draft made" body={draftProblem} />}
        {formula.ingredients.length === 0 ? <EmptyState title="No ingredients yet." /> : <RecordList label="Formula contents">
            {formula.ingredients.map(item => <IngredientRow key={item.ingredientId} item={item} warnings={formula.violations.filter(violation => violation.ingredientIds.includes(item.ingredientId)).map(violation => violation.note)} outcome={outcomes[item.ingredientId]} editable={editable} busy={busy} onDose={dose => changeDose(item.ingredientId, dose)} onType={() => setOutcomeOf(item.ingredientId, null)} onRemove={() => {
          setRemoving(item);
          setAsking("remove");
        }} />)}
          </RecordList>}
        {formula.missingMarkers.length === 0 ? null : <Text tone="secondary">
            Markers the rules wanted that are not in the labs:{" "}
            {formula.missingMarkers.map(markerName).join("; ")}.
          </Text>}
        {editProblem === null ? null : <ErrorState title="Not saved" body={editProblem} />}
      </Section>
      {editable ? <AddIngredient unused={unused} busy={busy} problem={addProblem} onAdd={add} /> : null}
      {editable ? <SubmitSection licenceVerified={formula.licenceVerified} busy={busy && !saving} onSubmit={() => setAsking("submit")} /> : null}
      <Outcome note={note} />
      <FormulaQuestions asking={asking} removing={removing} onClose={() => setAsking(null)} onRemove={remove} onRegenerate={regenerate} onSubmit={submit} />
    </ClinicShell>;
}